import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { WorkflowPopupComponent } from '../../workflow-popup/workflow-popup.component';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { trackByValue } from 'src/@core/utils/track-by';
import { JobService } from 'src/@core/services/job/job.service';
import { AppService } from 'src/@core/services/app.service';

@Component({
  selector: 'hour4u-admin-run-outreach-popup',
  templateUrl: './run-outreach-popup.component.html',
  styleUrls: ['./run-outreach-popup.component.scss']
})
export class RunOutreachPopupComponent implements OnInit {
  selectedGoal: 'shortlist' | 'fixed' | 'interview' = 'shortlist';
  goalCounts = {
    shortlist: null,
    fixed: null,
    interview: null
  };
  isLoading: boolean;
  selectedWorkflow: any;
  process: any;
  autoScheduling: boolean = false;
  schedule: { start_time: string; pause_time: string; repeat_days: string[] } = { start_time: '', pause_time: '', repeat_days: [] };
  profile: any;
  hours: string[] = [];
  minutes: string[] = [];
  timeHour: string = '09';
  timeMinute: string = '00';
  timeMeridiem: 'AM' | 'PM' = 'AM';
  pauseHour: string = '09';
  pauseMinute: string = '30';
  pauseMeridiem: 'AM' | 'PM' = 'AM';
  repeatMode: 'everyday' | 'custom' = 'everyday';

  /** Goal, KPIs & duration step of the campaign, passed in by the opener. */
  campaignConfig: any = null;
  goalOutcomes: string[] = [];
  campaignGoalText = '';
  private readonly defaultGoalCount = 50;

  constructor(
    private dialogRef: MatDialogRef<RunOutreachPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private enterpriseAdminService: EnterpriseAdminService,
    private notify: NotificationService,
    private jobService: JobService,
    private appService: AppService
  ) { }

  ngOnInit() {
    this.loadCampaignGoals();
    const isApollo = this.data && (this.data.useApollo === true || this.data.useApollo === 'true');
    this.process = isApollo ? 'start:fetchLeadsUntilX' : 'start:fetchLeadsLocalUntilX';

    this.profile = this.appService.getCurrentAccount();

    // Pre-patch scheduling from incoming data (if provided)
    const incomingAuto = this.data && (this.data.autoScheduling === true || this.data.autoScheduling === 'true' || this.data.auto_scheduling === 'true');
    if (incomingAuto) {
      this.autoScheduling = true;
    }

    let schedulePatchedFromData = false;
    if (this.data && this.data.scheduleConfig) {
      try {
        const cfg = typeof this.data.scheduleConfig === 'string' ? JSON.parse(this.data.scheduleConfig) : this.data.scheduleConfig;
        if (cfg && typeof cfg === 'object') {
          if (cfg.start_time) {
            this.schedule.start_time = cfg.start_time;
          }
          if (cfg.pause_time) {
            this.schedule.pause_time = cfg.pause_time;
          }
          if (Array.isArray(cfg.repeat_days)) {
            this.schedule.repeat_days = cfg.repeat_days.map((d: string) => (d || '').toLowerCase());
            // Decide repeat mode based on days
            const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
            this.repeatMode = allDays.every(d => this.schedule.repeat_days.indexOf(d) > -1) ? 'everyday' : 'custom';
          }
          schedulePatchedFromData = true;
        }
      } catch (e) {
        console.warn('Invalid scheduleConfig passed to RunOutreachPopupComponent:', e);
      }
    }

    // initialize time parts
    this.hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    this.minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    this.initTimePartsFromValue();
    this.initPausePartsFromValue();
    if (!schedulePatchedFromData) {
      // If no schedule was provided, apply default repeat behavior
      this.onRepeatModeChange();
    }
  }
  /**
   * Seed the dialog from the campaign's Goal, KPIs & duration config so the
   * number here is the same target the campaign setup shows, not a hardcoded 50.
   */
  private loadCampaignGoals() {
    let cfg = this.data && this.data.campaignConfig;
    if (typeof cfg === 'string') {
      try {
        cfg = JSON.parse(cfg);
      } catch (e) {
        cfg = null;
      }
    }
    this.campaignConfig = cfg && typeof cfg === 'object' ? cfg : null;
    this.goalOutcomes = this.campaignConfig && Array.isArray(this.campaignConfig.goal_outcomes)
      ? this.campaignConfig.goal_outcomes.filter((o: any) => !!o)
      : [];
    this.campaignGoalText = (this.campaignConfig && this.campaignConfig.campaign_goal) || '';

    const target = this.campaignConfig ? Number(this.campaignConfig.target_quantity) : 0;
    this.goalCounts.shortlist = target > 0 ? target : this.defaultGoalCount;
  }

  /**
   * Keep Goal, KPIs & duration in step when the target is changed here. Merges into
   * the existing config so no other goal setting is overwritten; best effort only.
   */
  private syncTargetToCampaignConfig(target: number) {
    if (!this.campaignConfig || Number(this.campaignConfig.target_quantity) === target) {
      return;
    }
    if (!this.profile || !this.profile.entity || !this.profile.user || !this.data.projectId) {
      return;
    }
    const merged = Object.assign({}, this.campaignConfig, { target_quantity: target });
    this.jobService.updateProject({
      entityId: this.profile.entity.id,
      userId: this.profile.user.id,
      userName: this.profile.user.userMeta && this.profile.user.userMeta.name ? this.profile.user.userMeta.name : '',
      projectId: this.data.projectId,
      campaignConfig: merged
    }).subscribe(
      () => { },
      (err) => console.warn('Could not sync target to campaign config:', err)
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }

  selectGoal(goal: 'shortlist' | 'fixed' | 'interview') {
    this.selectedGoal = goal;
  }

  runOutreach() {
    const projectId = this.data.projectId;
    const entityId = this.data.entityId;
    const requiredLeads = this.goalCounts[this.selectedGoal];
    let process = this.process;

    if (!requiredLeads || requiredLeads <= 0 || Math.floor(requiredLeads) !== Number(requiredLeads)) {
      this.notify.showMessage('Please enter a whole number of goals (1 or more)', 3000);
      return;
    }
    this.syncTargetToCampaignConfig(Number(requiredLeads));

    // If scheduling is enabled and valid, save schedule via updateHoaProject
    if (this.autoScheduling && this.isScheduleValid()) {
      const formData = new FormData();
      if (this.profile && this.profile.entity) {
        formData.append('entityId', this.profile.entity.id);
        formData.append('entityEmail', this.profile.entity.email || '');
      }
      if (this.profile && this.profile.user) {
        formData.append('userId', this.profile.user.id);
        formData.append('userName', this.profile.user.userMeta && this.profile.user.userMeta.name ? this.profile.user.userMeta.name : '');
      }
      formData.append('projectId', projectId);
      formData.append('autoScheduling', 'true');
      formData.append('scheduleConfig', JSON.stringify({
        start_time: this.schedule.start_time,
        pause_time: this.schedule.pause_time,
        repeat_days: this.schedule.repeat_days,
        required_leads: requiredLeads,
        goal_outcomes: this.goalOutcomes
      }));

      this.isLoading = true;
      this.jobService.updateHoaProject(formData).subscribe(
        (res: any) => {
          this.isLoading = false;
          const msg = (res && (res.message || res.msg || res.status || res.text)) || 'Schedule saved successfully';
          this.notify.showMessage(msg, 3000);
          this.dialogRef.close({ changeTabTo: 'connected', workflowTabTriggered: true });
        },
        () => {
          this.isLoading = false;
          this.notify.showMessage('Failed to save schedule', 3000);
        }
      );
      return;
    }

    // Otherwise, run outreach immediately (original flow)
    const payload: any = {
      projectId,
      entityId,
      requiredLeads,
      process,
      // Goal-driven run: stop once this many leads reach one of the campaign's goal outcomes.
      targetQuantity: Number(requiredLeads),
      goalOutcomes: this.goalOutcomes
    };
    if (this.data.workflow) {
      payload['iteration'] = this.data.workflow.iteration;
      payload['leads_iteration'] = 10;
      payload['process'] = 'continue:fetchLeadsLocalUntilX';
      payload['pause_remove'] = 'true';
    }

    this.isLoading = true;
    this.enterpriseAdminService.runOutreach(payload).subscribe(
      () => {
        this.isLoading = false;
        this.notify.showMessage('Outreach started successfully', 3000);
        this.dialogRef.close({ changeTabTo: 'connected' });
      },
      () => {
        this.isLoading = false;
        this.notify.showMessage('Failed to start outreach. Please try again.', 3000);
      }
    );

    this.dialogRef.close({ changeTabTo: 'connected', workflowTabTriggered: true });
  }

  toggleDay(day: string) {
    const d = day.toLowerCase();
    const idx = this.schedule.repeat_days.indexOf(d);
    if (idx > -1) {
      this.schedule.repeat_days.splice(idx, 1);
    } else {
      this.schedule.repeat_days.push(d);
    }
  }

  isDaySelected(day: string): boolean {
    return this.schedule.repeat_days.indexOf(day.toLowerCase()) > -1;
  }

  isScheduleValid(): boolean {
    return !!this.schedule.start_time && !!this.schedule.pause_time && Array.isArray(this.schedule.repeat_days) && this.schedule.repeat_days.length > 0;
  }

  deleteInProgress: boolean = false;

  deleteSchedule() {
    const projectId = this.data.projectId;
    const formData = new FormData();
    if (this.profile && this.profile.entity) {
      formData.append('entityId', this.profile.entity.id);
      formData.append('entityEmail', this.profile.entity.email || '');
    }
    if (this.profile && this.profile.user) {
      formData.append('userId', this.profile.user.id);
      formData.append('userName', this.profile.user.userMeta && this.profile.user.userMeta.name ? this.profile.user.userMeta.name : '');
    }
    formData.append('projectId', projectId);
    formData.append('autoScheduling', 'false');

    this.isLoading = true;
    this.deleteInProgress = true;
    this.jobService.updateHoaProject(formData).subscribe(
      (res: any) => {
        this.isLoading = false;
        this.deleteInProgress = false;
        const msg = (res && (res.message || res.msg || res.status || res.text)) || 'Schedule deleted';
        this.notify.showMessage(msg, 3000);
        this.dialogRef.close({ changeTabTo: 'connected', workflowTabTriggered: true });
      },
      () => {
        this.isLoading = false;
        this.deleteInProgress = false;
        this.notify.showMessage('Failed to delete schedule', 3000);
      }
    );
  }

  private initTimePartsFromValue() {
    const value = this.schedule.start_time || '';
    if (!value) {
      // default 09:00 AM
      this.timeHour = '09';
      this.timeMinute = '00';
      this.timeMeridiem = 'AM';
      this.onTimePartChange();
      return;
    }
    const [hh, mm] = value.split(':');
    let h = parseInt(hh, 10);
    this.timeMeridiem = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    this.timeHour = h.toString().padStart(2, '0');
    this.timeMinute = (mm || '00').padStart(2, '0');
  }

  onTimePartChange() {
    // convert to 24h HH:MM
    const hourNum = parseInt(this.timeHour, 10) % 12 + (this.timeMeridiem === 'PM' ? 12 : 0);
    const hh24 = hourNum.toString().padStart(2, '0');
    const mm = this.timeMinute.padStart(2, '0');
    this.schedule.start_time = `${hh24}:${mm}`;
    // If pause time wasn't set yet, default to +30 minutes
    if (!this.schedule.pause_time) {
      this.setPauseTimeFromStartPlusMinutes(30);
    }
  }

  private initPausePartsFromValue() {
    const value = this.schedule.pause_time || '';
    if (!value) {
      // default pause = start + 30 min (or 09:30 AM if start missing)
      if (this.schedule.start_time) {
        this.setPauseTimeFromStartPlusMinutes(30);
      } else {
        this.pauseHour = '09';
        this.pauseMinute = '30';
        this.pauseMeridiem = 'AM';
        this.onPausePartChange();
      }
      return;
    }
    const [hh, mm] = value.split(':');
    let h = parseInt(hh, 10);
    this.pauseMeridiem = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    this.pauseHour = h.toString().padStart(2, '0');
    this.pauseMinute = (mm || '00').padStart(2, '0');
  }

  onPausePartChange() {
    const hourNum = parseInt(this.pauseHour, 10) % 12 + (this.pauseMeridiem === 'PM' ? 12 : 0);
    const hh24 = hourNum.toString().padStart(2, '0');
    const mm = this.pauseMinute.padStart(2, '0');
    this.schedule.pause_time = `${hh24}:${mm}`;
  }

  private setPauseTimeFromStartPlusMinutes(deltaMinutes: number) {
    const value = this.schedule.start_time || '';
    const [hh, mm] = value.split(':');
    const h = parseInt(hh || '0', 10);
    const m = parseInt(mm || '0', 10);
    const total = (h * 60 + m + deltaMinutes) % (24 * 60);
    const hh24 = Math.floor(total / 60).toString().padStart(2, '0');
    const mm2 = (total % 60).toString().padStart(2, '0');
    this.schedule.pause_time = `${hh24}:${mm2}`;

    // keep UI parts in sync
    let h12 = parseInt(hh24, 10);
    this.pauseMeridiem = h12 >= 12 ? 'PM' : 'AM';
    h12 = h12 % 12;
    if (h12 === 0) h12 = 12;
    this.pauseHour = h12.toString().padStart(2, '0');
    this.pauseMinute = mm2;
  }

  onRepeatModeChange() {
    if (this.repeatMode === 'everyday') {
      this.schedule.repeat_days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    } else {
      // When switching to custom we want a clean slate
      this.schedule.repeat_days = [];
    }
  }
}
